import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Metrics } from 'components/insights/constants';
import { formatPercentage, formatResponseTime } from 'components/insights/formatting';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useInboundReferralsInsightsStore } from 'stores/inboundReferralsInsightsStore';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../shared/slideout/MetricDetailLayout';
import { StatTile } from '../../shared/slideout/statTiles';
import { INBOUND_DIMENSION_LABELS } from '../constants';

import { buildOverlaidColumnChart, OVERLAID_COLUMN_CHART_HEIGHT } from './overlaidColumnChart';

type Props = {
  // Switch-aware: true during a View By switch. Used for the per-entity chart, which changes per View By.
  loading: boolean;
  // First-open only: the tiles read the grand-total rollup (same across View By), so they gate on this
  // and don't spinner on a switch.
  initialLoading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  data: AnalyticsResponseData;
};

// Metric drill for the Accepted rate tile: acceptance rate + avg decision time, over a stacked
// "received vs accepted" bar per View-By entity.
export default function AcceptedDetailHeader({ loading, initialLoading, rollups, data }: Props) {
  const slideoutDimension = useInboundReferralsInsightsStore((state) => state.slideoutDimension);

  const overall = rollups.overall.metrics;
  const acceptanceRate = overall.getCurrentDataPoint(Metrics.ACCEPTANCE_RATE, 0);
  const acceptedCount = overall.getCurrentDataPoint(Metrics.ACCEPTED_COUNT, 0) ?? 0;
  const totalReceived = overall.getCurrentDataPoint(Metrics.REFERRAL_COUNT, 0) ?? 0;
  const avgTimeToAcceptedSeconds = overall.getCurrentDataPoint(Metrics.TIME_TO_ACCEPTED_DECISION_SECONDS_AVERAGE, 0);

  const entityLabel = slideoutDimension ? (INBOUND_DIMENSION_LABELS[slideoutDimension] ?? '') : '';

  // Sort by acceptance rate desc — matches the breakdown table's default sort (same pattern as
  // network performance's ReadmissionRateHeader).
  const { chartOptions, scrollConfig, isEmpty } = useMemo(
    () =>
      buildOverlaidColumnChart({
        names: data.dimensions.getCurrentDimensionValuesByIndex(0),
        rates: data.metrics.getCurrentSeries(Metrics.ACCEPTANCE_RATE),
        baseSeries: { name: 'Total Received', values: data.metrics.getCurrentSeries(Metrics.REFERRAL_COUNT) },
        overlaySeries: {
          name: 'Total Accepted',
          values: data.metrics.getCurrentSeries(Metrics.ACCEPTED_COUNT),
          color: colors.green100,
        },
      }),
    [data]
  );

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label='Avg. Acceptance Rate'
            tone='green'
            loading={initialLoading}
            inlineAddendum
            primary={formatPercentage(acceptanceRate)}
            addendum={acceptanceRate != null ? `${acceptedCount} of ${totalReceived} referrals received` : undefined}
          />
          <StatTile
            label='Avg. Time to Acceptance'
            loading={initialLoading}
            primary={formatResponseTime(avgTimeToAcceptedSeconds)}
          />
        </>
      }
      chartTitle={`Acceptance Rate by ${entityLabel}`}
      chartLoading={loading}
      chartHeight={OVERLAID_COLUMN_CHART_HEIGHT}
      isEmpty={isEmpty}
      scrollConfig={scrollConfig}
      emptyMessage='No accepted referrals to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
